import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { fmt } from './ceGraphUtil';

// Неприятие риска γ модели порогов хеджа (ADR-088, поправка 2026-11-03) — правка прямо с вкладки «Граф». Чистый интерфейс к BFF
// POST /vector-clearing/hmodel-gamma: границы значений, предпросмотр порогов (оценка по закону b_т ∝ γ^(−1/3)), шаг ×2, интервал 15 мин и
// решение «нужно подтверждение» считает бэкенд; здесь только ввод, показ ответа и два подтверждения. Не путать с «γ skew» рядом
// (сдвиг якоря по позиции, ce_inv_skew_gamma) и с другими γ проекта.
const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';
const URL = `${API_BASE}/vector-clearing/hmodel-gamma`;

const errText = (e) => {
  const d = e && e.response && e.response.data;
  return (d && (d.message || d.error)) || (e && e.message) || 'не выполнено';
};

const HmodelGammaControl = ({ info, onApplied }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);       // { kind: 'ok' | 'warn' | 'err', text }
  const [preview, setPreview] = useState(null);

  const eff = info ? info.effective : null;
  const cfg = info ? info.configured : null;
  const unsupported = info && info.supported === false;
  const value = text.trim() === '' ? null : Number(text.replace(',', '.'));
  const valid = value !== null && Number.isFinite(value) && value > 0;

  const send = useCallback(async (body, onOk) => {
    setBusy(true);
    try {
      const r = await axios.post(URL, body, { timeout: 15000, headers: { 'Content-Type': 'application/json' } });
      onOk(r.data);
    } catch (e) {
      const d = e && e.response && e.response.data;
      if (d && d.error === 'gamma_confirmation_required') {
        // BFF назвал причины и выдал подписанный токен (минута, привязан к значению и причинам): подтверждение оператора повторяет запрос с ним
        if (d.confirm_token && window.confirm(`${d.message}\n\nПрименить всё равно?`)) {
          try { const r2 = await axios.post(URL, { ...body, confirm_token: d.confirm_token }, { timeout: 15000, headers: { 'Content-Type': 'application/json' } }); onOk(r2.data); }
          catch (e2) { setMsg({ kind: 'err', text: errText(e2) }); }
        } else setMsg({ kind: 'warn', text: d.confirm_token ? 'отменено' : d.message });
      } else setMsg({ kind: 'err', text: errText(e) });
    } finally { setBusy(false); }
  }, []);

  const check = () => send({ gamma: value, dry_run: true }, (d) => {
    setPreview(d.preview || null);
    setMsg(d.needs_confirmation ? { kind: 'warn', text: d.message } : { kind: 'ok', text: 'проверка пройдена, можно применять' });
  });
  const apply = () => send({ gamma: value }, (d) => {
    setPreview(d.preview || null);
    setMsg({ kind: 'ok', text: d.unchanged ? 'то же значение — ничего не изменено' : `записано ${fmt(d.configured, 4)}; ledger применит за ≈${d.applies_in_s} с, пороги на агентах обновятся за ≈${d.settles_in_min} мин` });
    if (onApplied) onApplied();
  });
  const toEnv = () => {
    if (!window.confirm('Вернуть γ окружения ledger (убрать значение оператора)?')) return;
    send({ gamma: null }, (d) => {
      setPreview(null);
      setMsg({ kind: 'ok', text: d.unchanged ? 'уже используется γ окружения' : 'γ окружения возвращено' });
      if (onApplied) onApplied();
    });
  };

  const hard = info && info.hard ? info.hard : null;
  const source = cfg !== null && cfg !== undefined ? 'задано оператором' : 'из окружения ledger';
  return (
    <div className="cg-gamma" data-testid="hmodel-gamma">
      <span className="cg-chip" title="Неприятие риска γ модели порогов хеджа (ADR-088): чем больше γ, тем уже пороги, больше хеджей и комиссии, меньше размах позиции. Не путать с «γ skew» (сдвиг якоря по позиции).">
        γ модели порогов хеджа:{' '}
        <b data-testid="gamma-effective">{eff ? fmt(eff, 4) : '—'}</b>
        {eff ? <span className="cg-muted"> ({source})</span> : <span className="cg-muted"> {info && info.effectiveSupported === false ? '(копия proto BFF старее ledger)' : '(модель выключена или ledger не отдаёт γ)'}</span>}
        {eff && cfg !== null && cfg !== undefined && Math.abs(cfg - eff) > 1e-12 && <span className="cg-warn-t"> · записано {fmt(cfg, 4)}: ledger применит за ≈6 с или отверг значение</span>}
      </span>
      {unsupported ? (
        <span className="cg-chip cg-warn" role="status">колонка ce_hmodel_gamma не создана в БД — правка недоступна</span>
      ) : (
        <span className="cg-chip" role="group" aria-label="Изменить γ модели порогов">
          новое γ{' '}
          <input type="text" inputMode="decimal" value={text} onChange={(ev) => setText(ev.target.value)} placeholder={hard ? `${hard[0]}…${hard[1]}` : '0.0094'}
            className="cg-gamma-in" aria-label="Новое γ" disabled={busy} />
          <button type="button" className="cg-seg" onClick={check} disabled={busy || !valid} title="Проверить значение и показать оценку порогов, ничего не записывая">Проверить</button>
          <button type="button" className="cg-seg" onClick={apply} disabled={busy || !valid} title="Записать в настройки: ledger подхватит за ≈6 с">Применить</button>
          <button type="button" className="cg-seg" onClick={toEnv} disabled={busy || cfg === null || cfg === undefined} title="Убрать значение оператора, вернуть γ окружения">К окружению</button>
        </span>
      )}
      {msg && <span className={`cg-chip ${msg.kind === 'err' ? 'cg-warn' : ''}`} role="status" data-testid="gamma-msg">{msg.text}</span>}
      {preview && preview.byAsset && (
        <span className="cg-chip" data-testid="gamma-preview" title="Оценка по текущим сырым порогам агентов-переводчиков: b_т ∝ γ^(−1/3). Пороги на агентах обновятся сразу после применения (расчёт идёт по 4 агента за такт).">
          порог тейкера b_т, тыс. USDT, сейчас → после:{' '}
          {Object.entries(preview.byAsset).map(([a, v]) => <span key={a}><b>{a}</b> {fmt(v.btRawNowK, 1)} → {fmt(v.btRawNewK, 1)}{' '}</span>)}
        </span>
      )}
    </div>
  );
};

export default HmodelGammaControl;
